import React from "react";

import {
  applyQueueClaimed,
  applyQueueClaimReleased,
  applyQueueableCalls,
  setQueueClaimStatus,
  type CallState,
} from "~/context/call/callState";
import { getApiErrorMessage } from "~/services/apiErrors";
import { queueClaim } from "~/services/calling/sidecarApi";
import type {
  QueueClaimedPayload,
  QueueClaimReleasedPayload,
  QueueClaimStatus,
} from "~/services/calling/types";
import { normalizeSocketIdentityValue } from "~/context/call/helpers";
import type { SipLifecycleIdentity } from "~/context/call/useSipLifecycle";
import {
  fetchActiveQueueClaims,
  fetchQueueableCalls,
  type QueueableCall,
} from "~/services/queueCallService";

const QUEUE_REFRESH_DEBOUNCE_MS = 300;

type UseCallQueueParams = {
  isAuthenticated: boolean;
  userSipExtension: string | null;
  userExtensionId?: string | null;
  sipIdentityRef: React.MutableRefObject<SipLifecycleIdentity>;
  setState: React.Dispatch<React.SetStateAction<CallState>>;
  reportError?: (message: string) => void;
};

export const useCallQueue = ({
  isAuthenticated,
  userSipExtension,
  userExtensionId,
  sipIdentityRef,
  setState,
  reportError,
}: UseCallQueueParams) => {
  const refreshTimerRef = React.useRef<number | null>(null);
  const refreshInFlightRef = React.useRef(false);
  const refreshQueuedRef = React.useRef(false);
  const resolveMyExtension = React.useCallback(
    () =>
      normalizeSocketIdentityValue(userSipExtension) ??
      normalizeSocketIdentityValue(userExtensionId) ??
      sipIdentityRef.current.extension ??
      null,
    [sipIdentityRef, userExtensionId, userSipExtension]
  );

  const clearRefreshTimer = React.useCallback(() => {
    if (refreshTimerRef.current == null) return;
    window.clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = null;
  }, []);

  const refreshQueueableCalls = React.useCallback(async () => {
    if (!isAuthenticated) return;

    if (refreshInFlightRef.current) {
      refreshQueuedRef.current = true;
      return;
    }

    refreshInFlightRef.current = true;
    try {
      const [calls, claims] = await Promise.all([
        fetchQueueableCalls(),
        fetchActiveQueueClaims().catch(() => []),
      ]);
      const myExtension = resolveMyExtension();
      setState((prev) => {
        let next = applyQueueableCalls(prev, calls);
        for (const claim of claims) {
          next = applyQueueClaimed(
            next,
            claim.uniqueId,
            claim.extension,
            myExtension
          );
        }
        return next;
      });
    } catch (error) {
      console.warn("[CallQueue] Failed to refresh queueable calls", error);
    } finally {
      refreshInFlightRef.current = false;
      if (refreshQueuedRef.current) {
        refreshQueuedRef.current = false;
        void refreshQueueableCalls();
      }
    }
  }, [isAuthenticated, resolveMyExtension, setState]);

  const scheduleQueueRefresh = React.useCallback(() => {
    clearRefreshTimer();
    refreshTimerRef.current = window.setTimeout(() => {
      refreshTimerRef.current = null;
      void refreshQueueableCalls();
    }, QUEUE_REFRESH_DEBOUNCE_MS);
  }, [clearRefreshTimer, refreshQueueableCalls]);

  React.useEffect(() => () => clearRefreshTimer(), [clearRefreshTimer]);

  const refreshQueueableCallsRef = React.useRef(refreshQueueableCalls);
  refreshQueueableCallsRef.current = refreshQueueableCalls;

  React.useEffect(() => {
    if (!isAuthenticated) return;
    void refreshQueueableCallsRef.current();
  }, [isAuthenticated]);

  const handleQueueClaimed = React.useCallback(
    (payload: QueueClaimedPayload) => {
      const myExtension = resolveMyExtension();
      setState((prev) =>
        applyQueueClaimed(
          prev,
          payload.uniqueId,
          payload.extension,
          myExtension
        )
      );
    },
    [resolveMyExtension, setState]
  );

  const handleQueueClaimReleased = React.useCallback(
    (payload: QueueClaimReleasedPayload) => {
      setState((prev) => applyQueueClaimReleased(prev, payload.uniqueId));
    },
    [setState]
  );

  const pickUpQueueCall = React.useCallback(
    async (uniqueId: string) => {
      const extension = resolveMyExtension()?.trim();
      const normalizedUniqueId = uniqueId.trim();
      if (!extension) {
        throw new Error("No SIP extension configured.");
      }
      if (!normalizedUniqueId) {
        throw new Error("Call identity missing.");
      }

      setState((prev) =>
        setQueueClaimStatus(prev, normalizedUniqueId, "claiming")
      );

      try {
        await queueClaim(extension, normalizedUniqueId);
        setState((prev) =>
          setQueueClaimStatus(prev, normalizedUniqueId, "claimed-by-me")
        );
      } catch (error) {
        setState((prev) =>
          setQueueClaimStatus(prev, normalizedUniqueId, "available")
        );
        const parsed = getApiErrorMessage(error, "Unable to claim call.");
        reportError?.(parsed.message);
        throw error;
      }
    },
    [reportError, resolveMyExtension, setState]
  );

  return {
    scheduleQueueRefresh,
    refreshQueueableCalls,
    handleQueueClaimed,
    handleQueueClaimReleased,
    pickUpQueueCall,
  };
};

export const isQueueClaimDisabled = (status: QueueClaimStatus) =>
  status === "claiming" || status === "claimed-by-other";

export type ScreenedQueueableCall = QueueableCall & {
  customerId?: string | null;
  customerName?: string | null;
  matchType?: "customer" | "emergency-contact" | null;
  displayNumber: string;
  claimStatus: QueueClaimStatus;
};
